import type { UiTranslationKey } from './ui-translations';
import { talkRadiusOptions } from './distance-units';
import {
  createSeededTagOppositePairRegistryState,
  getOppositeTagName,
  registerOppositeTagPair,
  type TagOppositePairRegistryState,
} from '../../shared/tag-opposite-pairs';
import {
  getTagOppositePairRegistryState,
  setTagOppositePairRegistryState,
} from './answer-preferences-storage';
import { mountTalkPreviewPanel, type TalkPreviewCollectors } from './talk-editor-preview';
import { renderMediaTile } from './attachment-metadata';

// docs/TODO.md §LL: shared auto-fill/lock/preview behavior for a `type: 'tag'` talk's tag-word
// input (`#talk-title`) and its accepted-answer counterpart (`#talk-answer`). Typing into the
// source keeps auto-driving the answer field with the seeded opposite tag (buy → sell, live)
// until the author gives the answer field a value of their own — either by typing into it
// directly, or by opening the editor on an existing Pair-tag talk. With no known opposite, the
// answer defaults to the SAME word as the source (self-match — "Tennis" matches anyone else
// tagged "Tennis"), not a blank "matches anyone" state; §LL retired that as a distinct concept.
function wireTagAnswerAutoFill(config: {
  sourceInput: HTMLInputElement | null;
  answerInput: HTMLInputElement | null;
  previewEl: HTMLElement | null;
  registry: TagOppositePairRegistryState;
  initiallyLocked: boolean;
  selfMatchPreviewText: (tag: string) => string;
  oppositePreviewText: (answer: string) => string;
  /** docs/TODO.md §BB — fired when the author finishes typing (blurs) a genuinely divergent
   *  answer they typed themselves (an opposite pair, not a self-match), so the caller can
   *  persist it for future autofill. Never fired for a self-match — that's already the
   *  default fallback, no new pair to learn. */
  onCustomPairConfirmed?: (tagName: string, oppositeTagName: string) => void;
}): void {
  let locked = config.initiallyLocked;

  const renderPreview = (): void => {
    if (!config.previewEl) return;
    const sourceValue = config.sourceInput?.value.trim() || '';
    if (!sourceValue) {
      config.previewEl.textContent = '';
      return;
    }
    const answerValue = config.answerInput?.value.trim() || sourceValue;
    config.previewEl.textContent = answerValue === sourceValue
      ? config.selfMatchPreviewText(sourceValue)
      : config.oppositePreviewText(answerValue);
  };

  const onSourceInput = (): void => {
    const sourceValue = config.sourceInput?.value.trim() || '';
    if (sourceValue && config.answerInput && !locked) {
      const opposite = getOppositeTagName(config.registry, sourceValue);
      config.answerInput.value = opposite || sourceValue;
    }
    renderPreview();
  };

  config.sourceInput?.addEventListener('input', onSourceInput);
  config.answerInput?.addEventListener('input', () => {
    locked = true;
    renderPreview();
  });
  config.answerInput?.addEventListener('blur', () => {
    const sourceValue = config.sourceInput?.value.trim() || '';
    const answerValue = config.answerInput?.value.trim() || '';
    if (sourceValue && answerValue && answerValue !== sourceValue) {
      config.onCustomPairConfirmed?.(sourceValue, answerValue);
    }
  });
  onSourceInput();
}

type TalkEditorDialogOptions = {
  existingTalk?: any;
  /**
   * docs/TODO.md §Y1 — whether `existingTalk` (if any) is already authored by the person
   * opening the editor. When it isn't (a copied-but-unedited incoming talk), the form must
   * NOT submit through the "update in place" path — that would either silently fail (this
   * user never locally created the source talk) or, worse, save an edited version while still
   * leaving `authorId` as the original sender's (`WebTalkService.updateTalk` deliberately never
   * reassigns authorship, correct for a real self-owned edit, wrong here). Editing a copy must
   * go through the same mint-a-new-talk-and-transfer-credit path §V already built.
   */
  currentUserId?: string;
  escapeHtml: (text: string) => string;
  getAnswerPreferences: () => Record<string, any>;
  addQuestionToForm: (index: number, container: HTMLElement) => void;
  addAnswerToQuestion: (container: HTMLElement, index: number) => void;
  appendIgnoreRow: (container: HTMLElement, qIndex: number) => void;
  applyBuiltInKindToQuestion: (questionItem: HTMLElement, kind: string) => void;
  applyTagKindVisibilityToQuestion: (questionItem: HTMLElement) => void;
  updateAllAnswerDropdowns: () => void;
  refreshFlowAnswerConstraints: (type: string) => void;
  ensureRouteEditorRendered: (existingTalk?: any) => void;
  setupTalkFormHandlers: (modal: HTMLElement) => void;
  syncAdultLockFromBuiltInKinds: (root: ParentNode) => void;
  /** Only rendered/wired for a genuinely fresh create (no `existingTalk` at all — not shown when
   *  editing, copying, or already on a picked template): closes this blank editor and opens the
   *  template picker (`showTalkTemplatePicker`, ui-manager.ts). */
  onBrowseTemplates?: () => void;
  previewCollectors: TalkPreviewCollectors;
  text?: (key: UiTranslationKey) => string;
  /** docs/TODO.md §DD: fetches this device's own locally-published bytes for an `.ipfs-attachment`
   *  tile (spec §30.6's "Photo to share when matched" preview) so its thumbnail renders without a
   *  network round-trip — the author always holds their own attachment's bytes locally already. */
  hydrateAttachmentImages?: (container: HTMLElement) => void;
};

export function showTalkEditorDialog(options: TalkEditorDialogOptions): void {
  const { existingTalk } = options;
  const text = (key: UiTranslationKey, fallback: string): string => options.text?.(key) || fallback;
  const format = (key: UiTranslationKey, fallback: string, values: Record<string, string | number>): string =>
    Object.entries(values).reduce(
      (label, [placeholder, value]) => label.replace(`{${placeholder}}`, String(value)),
      text(key, fallback),
    );
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'talk-editor-modal';
  // docs/TODO.md §Y1: only a talk this user already authored counts as "editing in place" —
  // a copied-but-not-yet-edited talk (existingTalk.authorId belongs to someone else) still
  // pre-fills the form, but must fall through to the create-a-new-talk path on submit.
  const isOwnedEdit = !!(existingTalk?.id && (!existingTalk.authorId || existingTalk.authorId === options.currentUserId));
  if (isOwnedEdit) {
    (modal as HTMLElement).dataset.editingTalkId = existingTalk.id;
  }

  const renderForm = (): void => {
    const isEdit = !!(existingTalk && existingTalk.id);
    // docs/TODO.md §DD, spec §30.6: once attached at creation, the file input stays hidden on
    // edit (re-attaching/replacing isn't supported yet) — this read-only tile is what lets the
    // author still see the choice they already made ("authored content like any other
    // criterion, not a special-cased feature").
    const existingAttachment = Array.isArray(existingTalk?.ipfsAttachments) ? existingTalk.ipfsAttachments[0] : null;
    const existingAttachmentMarkup = existingAttachment
      ? renderMediaTile({
          cid: String(existingAttachment.cid || ''),
          link: `ipfs://${existingAttachment.cid || ''}`,
          name: String(existingAttachment.name || 'attachment'),
          mimeType: String(existingAttachment.mimeType || ''),
          sizeBytes: Number(existingAttachment.sizeBytes) || 0,
        })
      : '';
    modal.innerHTML = `
      <div class="modal-content size-xl modal-fullscreen talk-editor-shell">
        <div class="modal-header talk-editor-header">
          <div>
            <h2 class="modal-title">${isEdit ? text('editorEditTitle', 'Edit Talk') : text('editorCreateTitle', 'Create a Talk')}</h2>
            <p class="talk-editor-promise">${text('editorPromise', 'Write conversations, not code.')}</p>
          </div>
          <button type="button" class="close-button talk-editor-close" id="close-talk-editor-btn" aria-label="${text('close', 'Close')}">&times;</button>
        </div>
        <form id="talk-editor-form" class="talk-editor-form" data-editing-talk-id="${isOwnedEdit ? existingTalk.id : ''}" data-revise-source-talk="${!isOwnedEdit && existingTalk?.id ? options.escapeHtml(JSON.stringify(existingTalk)) : ''}">
          <p class="talk-editor-description">${text('editorDescription', 'Build a branching conversation flow - each answer can lead to a different question')}</p>
          ${!existingTalk ? `
          <div class="form-group">
            <button type="button" class="btn btn-secondary" id="browse-talk-templates-btn" style="width: 100%;">${text('editorBrowseTemplates', '🎨 Start from a template')}</button>
          </div>
          ` : ''}
          <fieldset class="form-group talk-type-picker">
            <legend class="form-label">${text('editorType', 'Type')}</legend>
            <div class="talk-type-grid">
              <label class="talk-type-option">
                <input type="radio" name="talk-type-radio" value="tag" ${existingTalk?.type === 'tag' || !existingTalk ? 'checked' : ''}>
                <span class="talk-type-icon" aria-hidden="true">#</span>
                <span>${text('editorTagTypeName' as UiTranslationKey, 'Tag')}</span>
              </label>
              <label class="talk-type-option">
                <input type="radio" name="talk-type-radio" value="flow" ${existingTalk?.type === 'flow' ? 'checked' : ''}>
                <span class="talk-type-icon" aria-hidden="true">→</span>
                <span>${text('editorFlowTypeName' as UiTranslationKey, 'Flow')}</span>
              </label>
              <label class="talk-type-option">
                <input type="radio" name="talk-type-radio" value="survey" ${existingTalk?.type === 'survey' ? 'checked' : ''}>
                <span class="talk-type-icon" aria-hidden="true">☷</span>
                <span>${text('editorSurveyTypeName' as UiTranslationKey, 'Survey')}</span>
              </label>
              <label class="talk-type-option">
                <input type="radio" name="talk-type-radio" value="route" ${existingTalk?.type === 'route' ? 'checked' : ''}>
                <span class="talk-type-icon" aria-hidden="true">⑂</span>
                <span>${text('editorRouteTypeName' as UiTranslationKey, 'Route')}</span>
              </label>
            </div>
            <select class="form-input" id="talk-type" aria-hidden="true" style="position: absolute; left: -9999px;" tabindex="-1">
              <option value="tag">Tag</option>
              <option value="flow">Flow</option>
              <option value="survey">Survey</option>
              <option value="route">Route</option>
            </select>
          </fieldset>

          <div class="form-group">
            <label class="form-label">${text('editorTalkTitle', 'Talk Title')}</label>
            <input type="text" class="form-input" id="talk-title" placeholder="${text('editorTitlePlaceholder', 'e.g., Coffee Meetup, Quick Survey')}" required value="${existingTalk ? options.escapeHtml(existingTalk.title) : ''}">
          </div>

          <div class="form-group" id="tag-like-group" style="display: none;">
            <label class="talk-send-chatroom-label" style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
              <input type="checkbox" id="tag-like-checkbox" checked aria-label="${text('editorTagLike', 'I like this tag')}">
              <span>${text('editorTagLike', 'I like this tag')}</span>
            </label>
          </div>

          <div class="form-group" id="tag-pair-group" style="display: none;">
            <label class="talk-send-chatroom-label" style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
              <input type="checkbox" id="tag-pair-checkbox" ${existingTalk?.type === 'tag' && existingTalk?.questions?.[0]?.reciprocalTagContext ? 'checked' : ''} aria-label="${text('editorTagPairCheckboxLabel' as UiTranslationKey, 'Pair tag (accepted answer differs, e.g. buy/sell)')}">
              <span>${text('editorTagPairCheckboxLabel' as UiTranslationKey, 'Pair tag (accepted answer differs, e.g. buy/sell)')}</span>
            </label>
          </div>

          <div class="form-group" id="tag-answer-group" style="display: none;">
            <label class="form-label" for="talk-answer">${text('editorTagAnswerLabel' as UiTranslationKey, "Accepted answer (optional — defaults to the tag word itself)")}</label>
            <input
              type="text"
              class="form-input"
              id="talk-answer"
              value="${options.escapeHtml(existingTalk?.questions?.[0]?.answers?.find((a: any) => a.isMatch)?.text || '')}"
              placeholder="${text('editorTagAnswerPlaceholder' as UiTranslationKey, "e.g. sell — or leave as the same word to match fellow buy people")}"
            >
            <p id="talk-answer-preview" style="margin: 6px 0 0 0; font-size: 0.85em; color: var(--text-secondary);"></p>
          </div>

          <div class="form-group" id="questions-form-group">
            <label class="form-label" id="questions-form-label">${text('editorQuestions', 'Questions & Branching')}</label>
            <p class="talk-editor-type-hint" id="talk-editor-type-hint" style="margin: 0 0 10px 0; font-size: 0.9em; color: var(--text-secondary);"></p>
            <div id="questions-container"></div>
            <button type="button" id="add-question-btn" class="btn" style="margin-top: 10px; background: var(--accent); color: white;">${text('editorAddQuestion', '+ Add Question')}</button>
          </div>

          <div class="form-group" id="route-form-group" style="display: none;">
            <label class="form-label">${text('editorRouteTitle', 'Route (DAG editor)')}</label>
            <p style="margin: 0 0 10px 0; font-size: 0.9em; color: var(--text-secondary);">
              ${text('editorRouteHelp', 'Build a branching tree. Each answer can lead to a follow-up question. On any path from root to leaf, the same question cannot appear twice; it may appear in separate branches with separate context ids.')}
            </p>
            <div id="route-editor"></div>
            <label class="form-label" for="talk-match-threshold" style="margin-top: 14px; display: block;">${text('editorMatchThresholdLabel' as UiTranslationKey, 'Match threshold (optional)')}</label>
            <p style="margin: 0 0 8px 0; font-size: 0.85em; color: var(--text-secondary);">
              ${text('editorMatchThresholdHelp' as UiTranslationKey, 'Treats every direct branch off the root as an independent spec, answerable in any order. A responder matches once at least this many specs match — a partial match still counts. Leave blank for the default: only the terminal answer on the path taken decides.')}
            </p>
            <input
              type="number"
              class="form-input"
              id="talk-match-threshold"
              min="1"
              step="1"
              value="${existingTalk?.matchThreshold != null ? existingTalk.matchThreshold : ''}"
              placeholder="${text('editorMatchThresholdPlaceholder' as UiTranslationKey, 'e.g. 2')}"
              style="max-width: 140px;"
            >
            <div id="talk-validation-errors" class="talk-validation-errors" style="display: none; margin-top: 10px; padding: 10px; border: 1px solid var(--danger); background: var(--danger-soft); color: var(--danger-hover); border-radius: 6px; font-size: 0.9em;"></div>
          </div>

          <div class="form-group" id="talk-validation-group" style="display: none;">
            <div id="talk-autofix-banner" class="talk-autofix-banner" style="display: none; margin-top: 10px; padding: 10px; border: 1px solid var(--success); background: var(--success-soft); color: var(--success-text); border-radius: 6px; font-size: 0.9em;"></div>
          </div>

          <details class="talk-editor-more" id="talk-editor-more" ${isEdit ? 'open' : ''}>
            <summary>${text('editorMoreOptions' as UiTranslationKey, 'More options')}</summary>
            <div class="talk-editor-more-body">
          <div class="form-group" id="talk-options-group">
            <label class="form-label">${text('editorExpiration', 'Expiration')}</label>
            <select class="form-input" id="talk-expires" aria-label="Talk expiration">
              <option value="">${text('editorForever', 'Forever')}</option>
              <option value="1y">${text('editorOneYear', 'One year')}</option>
              <option value="1M">${text('editorOneMonth', 'One month')}</option>
              <option value="1w">${text('editorOneWeek', 'One week')}</option>
              <option value="1d">${text('editorOneDay', 'One day')}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label" for="talk-collection-importance">${text('matchCollectionImportanceLabel' as UiTranslationKey, 'Collection priority')}</label>
            <select class="form-input" id="talk-collection-importance">
              <option value="">${text('matchCollectionImportanceNormal' as UiTranslationKey, 'Normal')}</option>
              <option value="important" ${existingTalk?.collectionImportance === 'important' ? 'selected' : ''}>${text('matchCollectionImportanceImportant' as UiTranslationKey, 'Important')}</option>
              <option value="must-have" ${existingTalk?.collectionImportance === 'must-have' ? 'selected' : ''}>${text('matchCollectionImportanceMustHave' as UiTranslationKey, 'Must-have')}</option>
            </select>
          </div>
          <div class="form-group" id="talk-location-group">
            <label class="form-label">${text('editorLocation', 'Location')}</label>
            <select class="form-input" id="talk-location-radius" aria-label="Location radius">
              <option value="">${text('editorAnywhere', 'Anywhere')}</option>
              ${talkRadiusOptions().map((option) => `<option value="${option.miles}">${option.label}</option>`).join('')}
            </select>
          </div>
          <div class="form-group" id="talk-send-chatroom-group" style="display: ${isEdit ? 'none' : 'block'};">
            <label class="talk-send-chatroom-label" style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
              <input type="checkbox" id="talk-send-to-chatroom" checked aria-label="Send to Chatroom">
              <span>${text('editorSendChatroom', 'Send to Chatroom')}</span>
            </label>
          </div>
          <div class="form-group" id="talk-adult-group">
            <label class="talk-send-chatroom-label" style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
              <input type="checkbox" id="talk-is-adult" aria-label="Adult content (18+)" ${existingTalk?.isAdult ? 'checked' : ''}>
              <span>🔞 ${text('editorAdult', 'Adult content (18+) - only delivered to age-verified users')}</span>
            </label>
          </div>
          <div class="form-group" id="talk-existing-attachment-group" style="display: ${existingAttachmentMarkup ? 'block' : 'none'};">
            <label class="form-label">${text('editorExistingAttachment', 'Photo to share when matched')}</label>
            <div id="talk-existing-attachment-preview">${existingAttachmentMarkup}</div>
          </div>
          <div class="form-group" id="talk-attachment-group" style="display: ${isEdit ? 'none' : 'block'};">
            <label class="form-label">${text('editorAttachment', 'Attach media (link shared automatically when someone matches)')}</label>
            <input type="file" class="form-input" id="talk-attachment-input" aria-label="Attach media to share on match">
            <div id="talk-attachment-name" style="margin-top: 6px; font-size: 0.85em; color: var(--text-tertiary);"></div>
          </div>
          <div class="form-group" id="talk-preview-panel"></div>
            </div>
          </details>
        </form>
          <div class="modal-actions talk-editor-actions">
            <button type="button" class="btn btn-secondary" id="cancel-talk-btn">${text('editorCancel', 'Cancel')}</button>
            <button type="submit" class="btn" id="talk-submit-btn" form="talk-editor-form">${isEdit ? text('editorSave', 'Save changes') : text('editorCreate', 'Create')}</button>
          </div>
      </div>
    `;

    const questionsContainer = document.getElementById('questions-container');
    if (questionsContainer) {
      questionsContainer.innerHTML = '';
      if (existingTalk && Array.isArray(existingTalk.questions) && existingTalk.questions.length > 0) {
        existingTalk.questions.forEach((q: any, qIndex: number) => {
          options.addQuestionToForm(qIndex, questionsContainer);
          const questionItem = questionsContainer.querySelector(`[data-question-index="${qIndex}"]`);
          if (questionItem) {
            const textInput = questionItem.querySelector('.question-text') as HTMLInputElement | null;
            if (textInput) textInput.value = q.text || '';
            const importanceSelect = questionItem.querySelector('.question-collection-importance') as HTMLSelectElement | null;
            if (importanceSelect && (q.collectionImportance === 'important' || q.collectionImportance === 'must-have')) {
              importanceSelect.value = q.collectionImportance;
            }
            if (q.reciprocalTagContext) {
              const reciprocalCheckbox = questionItem.querySelector('.question-reciprocal-tag') as HTMLInputElement | null;
              if (reciprocalCheckbox) reciprocalCheckbox.checked = true;
            }
            if (q.tagKind === 'simple') {
              const simpleCheckbox = questionItem.querySelector('.question-simple-tag') as HTMLInputElement | null;
              if (simpleCheckbox) simpleCheckbox.checked = true;
            }
            if (q.reciprocalTagContext || q.tagKind === 'simple') {
              options.applyTagKindVisibilityToQuestion(questionItem as HTMLElement);
            }
            // §BB / spec §30.2: rehydrate the builtIn kind selector + its typed value so
            // reopening the editor for a saved builtIn question doesn't silently reset to
            // "ordinary answer choices" (the answer-selection-mode toggle has this same gap
            // pre-existing from §FF — not fixed here, out of scope for this change).
            if (q.builtIn?.kind) {
              const kindSelect = questionItem.querySelector('.builtin-kind') as HTMLSelectElement | null;
              if (kindSelect) kindSelect.value = q.builtIn.kind;
              options.applyBuiltInKindToQuestion(questionItem as HTMLElement, q.builtIn.kind);
              if (q.builtIn.kind === 'quantity' && typeof q.builtIn.quantity === 'number') {
                const input = questionItem.querySelector('.builtin-quantity-input') as HTMLInputElement | null;
                if (input) input.value = String(q.builtIn.quantity);
              } else if (q.builtIn.kind === 'priceRange' && q.builtIn.priceRange) {
                const minInput = questionItem.querySelector('.builtin-pricerange-min') as HTMLInputElement | null;
                const maxInput = questionItem.querySelector('.builtin-pricerange-max') as HTMLInputElement | null;
                if (minInput) minInput.value = String(q.builtIn.priceRange.min);
                if (maxInput) maxInput.value = String(q.builtIn.priceRange.max);
              } else if (q.builtIn.kind === 'timeFrame' && q.builtIn.timeFrame) {
                const startInput = questionItem.querySelector('.builtin-timeframe-start') as HTMLInputElement | null;
                const endInput = questionItem.querySelector('.builtin-timeframe-end') as HTMLInputElement | null;
                if (startInput) startInput.value = new Date(q.builtIn.timeFrame.start).toISOString().slice(0, 10);
                if (endInput) endInput.value = new Date(q.builtIn.timeFrame.end).toISOString().slice(0, 10);
              } else if (q.builtIn.kind === 'ageRange' && q.builtIn.ageRange) {
                const ageInput = questionItem.querySelector('.builtin-agerange-age') as HTMLInputElement | null;
                const minInput = questionItem.querySelector('.builtin-agerange-min') as HTMLInputElement | null;
                const maxInput = questionItem.querySelector('.builtin-agerange-max') as HTMLInputElement | null;
                if (ageInput) ageInput.value = String(q.builtIn.ageRange.age);
                if (minInput) minInput.value = String(q.builtIn.ageRange.acceptableRange.min);
                if (maxInput) maxInput.value = String(q.builtIn.ageRange.acceptableRange.max);
              }
            }
            // Progressive disclosure: the advanced fields above (answer-selection-mode, Simple/
            // Pair tag, builtIn) live inside a collapsed <details> by default — open it whenever
            // any of them already has a non-default value, so reopening a saved talk (or a
            // picked template) never hides a value the author would have to hunt for.
            if (q.reciprocalTagContext || q.tagKind === 'simple' || q.builtIn?.kind || q.answerSelectionMode === 'multiple' || q.collectionImportance) {
              const advancedDetails = questionItem.querySelector('.question-advanced') as HTMLDetailsElement | null;
              if (advancedDetails) advancedDetails.open = true;
            }
            const answersContainer = questionItem.querySelector('.answers-container') as HTMLElement | null;
            if (answersContainer && Array.isArray(q.answers)) {
              answersContainer.innerHTML = '';
              q.answers.forEach((a: any, aIndex: number) => {
                options.addAnswerToQuestion(answersContainer, aIndex);
                const answerItem = answersContainer.querySelector(`[data-answer-index="${aIndex}"]`);
                if (answerItem) {
                  const answerInput = answerItem.querySelector('.answer-text') as HTMLInputElement | null;
                  if (answerInput) answerInput.value = a.text || '';
                }
              });
              options.appendIgnoreRow(answersContainer, qIndex);
            }
          }
        });
        options.updateAllAnswerDropdowns();
        existingTalk.questions.forEach((q: any, qIndex: number) => {
          const questionItem = questionsContainer.querySelector(`[data-question-index="${qIndex}"]`);
          if (!questionItem || !Array.isArray(q.answers)) return;
          const answersContainer = questionItem.querySelector('.answers-container');
          if (!answersContainer) return;
          const answerItems = answersContainer.querySelectorAll('.answer-item');
          q.answers.forEach((a: any, aIndex: number) => {
            const answerItem = answerItems[aIndex];
            const nextSelect = answerItem?.querySelector('.answer-next') as HTMLSelectElement | null;
            if (!nextSelect) return;
            if (a.isIgnore) nextSelect.value = 'ignore';
            else if (a.isMatch) nextSelect.value = 'noticed';
            else if (a.nextQuestionId) nextSelect.value = a.nextQuestionId;
          });
        });
        const editingId = existingTalk.id;
        if (editingId) {
          const prefs = options.getAnswerPreferences();
          existingTalk.questions.forEach((q: any, qIndex: number) => {
            const questionId = q.id || `q_${qIndex}`;
            const key = `${editingId}_${questionId}`;
            const pref = prefs[key];
            const questionItem = questionsContainer.querySelector(`[data-question-index="${qIndex}"]`);
            const radio = questionItem?.querySelector(`input[name="self-answer-q_${qIndex}"][value="${pref?.answerId}"]`) as HTMLInputElement | null;
            if (radio) {
              radio.checked = true;
            } else {
              const ignoreRadio = questionItem?.querySelector('.self-answer-ignore-row input[value="ignore"]') as HTMLInputElement | null;
              if (ignoreRadio) ignoreRadio.checked = true;
            }
          });
        }
      } else {
        options.addQuestionToForm(0, questionsContainer);
      }
    }

    const expiresSelect = document.getElementById('talk-expires') as HTMLSelectElement | null;
    const locationSelect = document.getElementById('talk-location-radius') as HTMLSelectElement | null;
    if (existingTalk) {
      if (expiresSelect && existingTalk.expiresAt != null) {
        const exp = Number(existingTalk.expiresAt);
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;
        if (now > exp) expiresSelect.value = '';
        else if (exp - now <= oneDay) expiresSelect.value = '1d';
        else if (exp - now <= 7 * oneDay) expiresSelect.value = '1w';
        else if (exp - now <= 30 * oneDay) expiresSelect.value = '1M';
        else if (exp - now <= 365 * oneDay) expiresSelect.value = '1y';
        else expiresSelect.value = '';
      }
      if (locationSelect && existingTalk.locationRadiusMiles != null) {
        // Pick the closest offered radius: a Talk saved in the other unit converts approximately.
        const saved = Number(existingTalk.locationRadiusMiles);
        const closest = talkRadiusOptions()
          .reduce((best, option) => Math.abs(option.miles - saved) < Math.abs(best.miles - saved) ? option : best);
        locationSelect.value = String(closest.miles);
      }
    }

    const talkOptionsGroup = document.getElementById('talk-options-group');
    const talkLocationGroup = document.getElementById('talk-location-group');
    const talkSendChatroomGroup = document.getElementById('talk-send-chatroom-group');
    const tagLikeGroup = document.getElementById('tag-like-group');
    const tagLikeCheckbox = document.getElementById('tag-like-checkbox') as HTMLInputElement | null;
    const tagPairGroup = document.getElementById('tag-pair-group');
    const tagPairCheckbox = document.getElementById('tag-pair-checkbox') as HTMLInputElement | null;
    const tagAnswerGroup = document.getElementById('tag-answer-group');
    const talkTypeSelect = document.getElementById('talk-type') as HTMLSelectElement | null;
    const questionsFormGroup = document.getElementById('questions-form-group');
    const routeFormGroup = document.getElementById('route-form-group');
    const advancedOptions = document.getElementById('talk-editor-more') as HTMLDetailsElement | null;
    const questionsTypeHint = document.getElementById('talk-editor-type-hint');
    const questionsFormLabel = document.getElementById('questions-form-label');

    const updateFormForType = (): void => {
      const type = talkTypeSelect?.value || 'tag';
      const titleInput = document.getElementById('talk-title') as HTMLInputElement | null;
      const desc = modal.querySelector('.talk-editor-description');
      if (questionsFormGroup) questionsFormGroup.style.display = 'none';
      if (routeFormGroup) routeFormGroup.style.display = 'none';
      if (tagLikeGroup) tagLikeGroup.style.display = 'none';
      if (tagPairGroup) tagPairGroup.style.display = 'none';
      if (tagAnswerGroup) tagAnswerGroup.style.display = 'none';
      if (talkOptionsGroup) talkOptionsGroup.style.display = 'none';
      if (talkLocationGroup) talkLocationGroup.style.display = 'none';
      if (talkSendChatroomGroup) talkSendChatroomGroup.style.display = 'none';
      if (questionsFormGroup) {
        questionsFormGroup.querySelectorAll('input, select, textarea').forEach((el) => {
          (el as HTMLInputElement).disabled = true;
        });
      }

      if (type === 'tag') {
        // A simple tag needs only a keyword and the two tag toggles. Keep optional controls one
        // tap away instead of forcing phone users to scroll through them before reaching Create.
        if (advancedOptions && !isEdit) advancedOptions.open = false;
        if (tagLikeGroup) tagLikeGroup.style.display = 'block';
        if (tagLikeCheckbox && !isEdit && tagLikeCheckbox.checked === false) tagLikeCheckbox.checked = true;
        if (tagPairGroup) tagPairGroup.style.display = 'block';
        // docs/TODO.md §LL follow-up: the accepted-answer field only matters once the author
        // opts into a Pair tag (divergent accepted answer, e.g. buy/sell) — a plain tag defaults
        // to self-match and hides this entirely, so `#talk-answer`'s value is never read at
        // submit time (ui-manager.ts) unless the checkbox is checked.
        if (tagAnswerGroup) tagAnswerGroup.style.display = tagPairCheckbox?.checked ? 'block' : 'none';
        if (titleInput) {
          titleInput.placeholder = text('editorTagPlaceholder', 'e.g., Coffee, Tennis, Jobs');
          titleInput.setAttribute('aria-label', text('editorTagKeyword', 'Tag keyword'));
        }
        if (desc) (desc as HTMLElement).textContent = text('editorTagDescription', 'Tag: one keyword. Others answer directly with Like or Pass.');
        return;
      }

      // Flow/survey/route authors commonly need delivery, expiry and preview controls while they
      // build the longer form. Keep those visible by default; they can still collapse the panel.
      if (advancedOptions) advancedOptions.open = true;
      if (talkOptionsGroup) talkOptionsGroup.style.display = 'block';
      if (talkLocationGroup) talkLocationGroup.style.display = 'block';
      if (talkSendChatroomGroup) talkSendChatroomGroup.style.display = isEdit ? 'none' : 'block';
      if (titleInput) {
        titleInput.placeholder = text('editorTitlePlaceholder', 'e.g., Coffee Meetup, Quick Survey');
        titleInput.removeAttribute('aria-label');
      }

      if (type === 'route') {
        if (routeFormGroup) routeFormGroup.style.display = 'block';
        if (desc) (desc as HTMLElement).textContent = text('editorRouteDescription', 'Route: a branching DAG. Each answer can lead to a follow-up question; the same question can appear in different branches with different context ids.');
        options.ensureRouteEditorRendered(existingTalk);
        return;
      }

      if (questionsFormGroup) {
        questionsFormGroup.style.display = 'block';
        questionsFormGroup.querySelectorAll('input, select, textarea').forEach((el) => {
          (el as HTMLInputElement).disabled = false;
        });
      }
      if (type === 'survey') {
        if (questionsFormLabel) questionsFormLabel.textContent = text('editorSurveyQuestions', 'Questions (independent)');
        if (questionsTypeHint) {
          questionsTypeHint.textContent = text('editorSurveyHint', 'Survey: questions are independent - no branching. Every answer has a counter used for aggregate statistics.');
        }
        if (desc) (desc as HTMLElement).textContent = text('editorSurveyDescription', 'Survey: independent question and answer pairs. Counts per answer are tallied for statistics.');
      } else {
        if (questionsFormLabel) questionsFormLabel.textContent = text('editorFlowQuestions', 'Questions (flow)');
        if (questionsTypeHint) {
          questionsTypeHint.textContent = text('editorFlowHint', 'Flow: each question must be unique. The first answer is your match or next-question decision; extra answers are treated as ignore.');
        }
        if (desc) (desc as HTMLElement).textContent = text('editorFlowDescription', 'Flow: a linear chain of unique questions - the first answer decides and the rest are ignored.');
      }
      options.refreshFlowAnswerConstraints(type);
    };

    modal.querySelectorAll('input[name="talk-type-radio"]').forEach((radio) => {
      radio.addEventListener('change', (event) => {
        const value = (event.target as HTMLInputElement).value;
        if (talkTypeSelect) talkTypeSelect.value = value;
        updateFormForType();
      });
    });
    const checkedRadio = modal.querySelector('input[name="talk-type-radio"]:checked') as HTMLInputElement | null;
    if (talkTypeSelect && checkedRadio) {
      talkTypeSelect.value = checkedRadio.value;
    }
    if (talkTypeSelect) {
      talkTypeSelect.addEventListener('change', updateFormForType);
      updateFormForType();
    }
    tagPairCheckbox?.addEventListener('change', updateFormForType);

    // docs/TODO.md §LL: `type: 'tag'` talks reframe the tag word (from `#talk-title`) as the
    // question and `#talk-answer` as the answer — auto-fill/lock/preview behavior (seeded
    // opposite if known, else the same word for self-match) wired via `wireTagAnswerAutoFill`.
    // docs/TODO.md §BB: seeded app-predefined pairs merged with whatever this author has
    // previously typed themselves (persisted separately, `answer-preferences-storage.ts`) —
    // so a custom pair the author declares once (e.g. "borrow"/"lend") auto-fills again on
    // the next talk without needing the 3 hardcoded seed pairs to know about it.
    let userTagRegistry = getTagOppositePairRegistryState();
    const tagRegistry: TagOppositePairRegistryState = {
      pairs: { ...createSeededTagOppositePairRegistryState().pairs, ...userTagRegistry.pairs },
    };
    const persistCustomTagPair = (tagName: string, oppositeTagName: string): void => {
      userTagRegistry = registerOppositeTagPair(userTagRegistry, tagName, oppositeTagName);
      setTagOppositePairRegistryState(userTagRegistry);
      tagRegistry.pairs = { ...createSeededTagOppositePairRegistryState().pairs, ...userTagRegistry.pairs };
    };
    // Mirrors the `#tag-pair-checkbox` initial-checked condition above: an existing tag-type
    // talk whose Pair tag was already set shouldn't have this auto-fill clobber the answer.
    const tagAnswerAlreadyLocked = existingTalk?.type === 'tag' && !!existingTalk?.questions?.[0]?.reciprocalTagContext;

    wireTagAnswerAutoFill({
      sourceInput: document.getElementById('talk-title') as HTMLInputElement | null,
      answerInput: document.getElementById('talk-answer') as HTMLInputElement | null,
      previewEl: document.getElementById('talk-answer-preview'),
      registry: tagRegistry,
      initiallyLocked: tagAnswerAlreadyLocked,
      selfMatchPreviewText: (tag) => format('editorTagPairNoOpposite', "Matches anyone also tagged '{tag}'.", { tag }),
      oppositePreviewText: (answer) => `${text('editorTagPairOppositeLabel', 'Shown to people tagged:')} ${answer}`,
      onCustomPairConfirmed: persistCustomTagPair,
    });

    options.setupTalkFormHandlers(modal);
    document.getElementById('close-talk-editor-btn')?.addEventListener('click', () => {
      if (document.body.contains(modal)) document.body.removeChild(modal);
    });
    // §DD: covers both a prefilled Dating template and reopening an existing ageRange talk for
    // edit — the per-question builtin-kind change listener (talk-editor-form-helpers.ts) handles
    // every subsequent edit from here.
    options.syncAdultLockFromBuiltInKinds(modal);
    mountTalkPreviewPanel(modal, options.previewCollectors, text);
    if (existingAttachmentMarkup) options.hydrateAttachmentImages?.(modal);

    document.getElementById('browse-talk-templates-btn')?.addEventListener('click', () => {
      if (document.body.contains(modal)) document.body.removeChild(modal);
      options.onBrowseTemplates?.();
    });
  };

  document.body.appendChild(modal);
  renderForm();
}
